import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield,
  Brain,
  BarChart3,
  FileText,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Zap,
  Lock,
  Globe,
  ChevronRight,
  Target,
  Calculator,
  Layers,
  Clock,
  DollarSign,
  LineChart,
  GitBranch,
  Compass,
  Gauge,
  ClipboardList,
  AlertTriangle,
  Users,
  Activity,
  Search,
  Scale,
  Download,
  Building2,
  Workflow,
  BarChart2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const MarketingSite = () => {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [activePillar, setActivePillar] = useState(0);

  const handleDemoRequest = async () => {
    const email = formData.email.trim();
    const name = formData.name.trim();
    if (!email || !name) {
      setError("Please enter your name and work email.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError("Something went wrong. Please try again or email us directly.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const pillars = [
    {
      icon: Compass,
      title: "Risk Intelligence & Decision Making",
      tagline: "Don't just identify risks — know what to do about them.",
      color: "indigo",
      colorClass: "text-indigo-400",
      borderClass: "border-indigo-500/30",
      bgClass: "bg-indigo-500/10",
      tools: [
        { icon: Brain, name: "Decision Coach", desc: "AI-guided decision frameworks for complex risk trade-offs" },
        { icon: GitBranch, name: "Scenario Planning", desc: "Model cascading effects, early warning signs, and mitigation paths" },
        { icon: Activity, name: "Monte Carlo Simulation", desc: "Probabilistic loss modeling with thousands of scenarios" },
        { icon: Target, name: "Strategic Risk Alignment", desc: "Map risks to objectives with AI-analyzed success probability" },
        { icon: GitBranch, name: "Bow-Tie Analysis", desc: "AI-generated cause-control-consequence diagrams in 15 seconds" },
        { icon: Search, name: "Emerging Risk Radar", desc: "External intelligence scanning for regulatory and market threats" },
      ],
    },
    {
      icon: Shield,
      title: "Operational Risk Management",
      tagline: "Your entire risk lifecycle in one connected system.",
      color: "cyan",
      colorClass: "text-cyan-400",
      borderClass: "border-cyan-500/30",
      bgClass: "bg-cyan-500/10",
      tools: [
        { icon: ClipboardList, name: "Risk Register & Scoring", desc: "Dynamic risk scoring with AI-calibrated assessments" },
        { icon: Shield, name: "Control Library", desc: "Centralized controls mapped to risks with effectiveness tracking" },
        { icon: Gauge, name: "KRI Monitoring & Alerts", desc: "Real-time indicators with threshold alerts and escalation workflows" },
        { icon: Workflow, name: "AI Mitigation Plans & Action Tracking", desc: "AI drafts mitigation strategies per risk — controls, owners, timelines, costs — then assigns, tracks, and closes them across teams" },
        { icon: Users, name: "Culture Assessments & Surveys", desc: "Calibrate risk scores with leadership input and organizational culture data" },
        { icon: FileText, name: "Board-Ready Reports", desc: "Executive summaries, risk snapshots, strategic overviews — one click" },
      ],
    },
    {
      icon: DollarSign,
      title: "Financial Risk Optimization",
      tagline: "Turn risk data into financial decisions.",
      color: "emerald",
      colorClass: "text-emerald-400",
      borderClass: "border-emerald-500/30",
      bgClass: "bg-emerald-500/10",
      tools: [
        { icon: BarChart3, name: "Total Cost of Risk", desc: "Premium spend, retained losses, admin costs — one unified TCOR view" },
        { icon: Scale, name: "Transfer Optimization", desc: "Retention analysis, ILF modeling, and optimal deductible selection" },
        { icon: Calculator, name: "Captive Feasibility & Pro Forma", desc: "5-year P&L, balance sheet, and cash flow projections for captive analysis" },
        { icon: FileText, name: "AI Manuscript Policy Studio", desc: "Draft captive manuscript policies for unique risks in minutes — AI-composed, counsel-ready with built-in review flags" },
        { icon: LineChart, name: "Loss Trending & Reserving", desc: "IBNR triangles, development factors, and bootstrap reserving — directional output to scope and stress-test actuarial work" },
        { icon: BarChart2, name: "Industry Benchmarks", desc: "Compare loss ratios across 10 NAIC coverage lines and 10 industries" },
        { icon: TrendingUp, name: "Domicile Intelligence", desc: "Compare captive domicile regulations, tax structures, and requirements" },
      ],
    },
    {
      icon: Building2,
      title: "Insurance Program Management",
      tagline: "Manage your entire insurance program with AI-assisted analysis.",
      color: "amber",
      colorClass: "text-amber-400",
      borderClass: "border-amber-500/30",
      bgClass: "bg-amber-500/10",
      tools: [
        { icon: Layers, name: "Insurance Tower Visualization", desc: "Layered coverage programs with attachment points and co-carriers" },
        { icon: FileText, name: "Policy Management", desc: "Full policy lifecycle — create, renew, track carriers, limits, and deductibles" },
        { icon: Brain, name: "AI Document Extraction", desc: "Upload policy docs and loss runs — AI extracts structured data automatically" },
        { icon: AlertTriangle, name: "Loss Run Management", desc: "Upload, store, and analyze loss history with trending insights" },
        { icon: DollarSign, name: "Premium Analytics", desc: "ILF tables, premium-at-limit calculations, and rate adequacy analysis" },
        { icon: BarChart3, name: "Efficiency Scoring", desc: "Program-wide efficiency metrics with cost-per-unit-of-coverage analysis" },
      ],
    },
  ];

  const comparisonPoints = [
    { label: "ERM program build-out", theirCost: "$150,000 – $500,000+", theirTime: "12 – 24 months", you: "Days", note: "Risk register, KRIs, bow-ties, controls, board reporting — live out of the box" },
    { label: "Risk mitigation plan development", theirCost: "$3,000 – $10,000 per risk", theirTime: "1 – 3 weeks per risk", you: "Seconds", note: "AI drafts controls, owners, costs, and timelines for every risk in your register" },
    { label: "Actuarial scoping & scenario runs", theirCost: "$25,000 – $75,000", theirTime: "6 – 12 weeks", you: "Minutes", note: "Directional — not a Statement of Actuarial Opinion" },
    { label: "Enterprise GRC implementation", theirCost: "$100,000 – $500,000", theirTime: "3 – 9 months", you: "Days" },
    { label: "Bow-Tie analysis workshop", theirCost: "$5,000 – $15,000", theirTime: "2 – 4 weeks", you: "15 seconds" },
    { label: "Board risk report preparation", theirCost: "$2,000 – $5,000", theirTime: "1 – 2 weeks", you: "1 click" },
  ];

  const active = pillars[activePillar];

  return (
    <div className="min-h-screen bg-[#06080f]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#06080f]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/540aeb3a-1114-4f99-bc37-f6fae5b5d364/images/ebc8ab9a8b9b87e3f3e4c74c7286555b4503165514e41967f2693644773011b6.png"
                alt="FractionalRisk.ai Logo"
                className="h-10 w-auto"
              />
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#pillars" className="text-sm text-slate-500 hover:text-white transition-colors">Platform</a>
              <a href="#roi" className="text-sm text-slate-500 hover:text-white transition-colors">ROI</a>
              <a href="#how-it-works" className="text-sm text-slate-500 hover:text-white transition-colors">How It Works</a>
              <Link to="/security" className="text-sm text-slate-500 hover:text-white transition-colors" data-testid="nav-security-link">Security</Link>
              <a href="#contact" className="text-sm text-slate-500 hover:text-white transition-colors">Contact</a>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button className="glow-primary" data-testid="marketing-login-btn">Login</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-indigo-600/6 rounded-full blur-[140px] pointer-events-none" />

        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-8">
              <Zap className="w-4 h-4 text-indigo-400" />
              <span className="text-sm text-indigo-300">AI-Powered Risk Optimization Platform</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight">
              <span className="text-white">Risk Decisions,</span>
              <br />
              <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                Financially Grounded.
              </span>
            </h1>

            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto mb-6 leading-relaxed">
              Most risk decisions are made without the financial picture. Most risk financing
              happens without the strategic context. FractionalRisk.ai is where they finally meet.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 max-w-3xl mx-auto mb-10 text-sm text-slate-300">
              <span>Strategic Risk</span>
              <span className="text-indigo-400/60">·</span>
              <span>Total Cost of Risk</span>
              <span className="text-indigo-400/60">·</span>
              <span>Transfer Optimization</span>
              <span className="text-indigo-400/60">·</span>
              <span>Captive Feasibility</span>
              <span className="text-indigo-400/60">·</span>
              <span>Board Reporting</span>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <a href="#contact">
                <Button size="lg" className="glow-primary text-base px-8 py-6" data-testid="hero-cta-btn">
                  Schedule a Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a href="#pillars">
                <Button size="lg" variant="outline" className="text-base px-8 py-6 border-white/10 hover:bg-white/5">
                  Explore the Platform
                </Button>
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/public/marketing-pdf`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="lg" variant="outline" className="text-base px-8 py-6 border-cyan-500/30 hover:bg-cyan-500/10 text-cyan-400" data-testid="download-overview-pdf-btn">
                  <Download className="w-5 h-5 mr-2" />
                  Platform Overview PDF
                </Button>
              </a>
            </div>

            {/* Hero Stats — one per pillar */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl mx-auto"
            >
              {[
                { value: "6", label: "Decision & Scenario Tools", color: "text-indigo-400", icon: Compass },
                { value: "20+", label: "Operational Risk Modules", color: "text-cyan-400", icon: Shield },
                { value: "5-Year", label: "Financial Projections", color: "text-emerald-400", icon: TrendingUp },
                { value: "Full", label: "Insurance Program Mgmt", color: "text-amber-400", icon: Building2 },
              ].map((stat, i) => (
                <div key={i} className="rounded-lg bg-white/[0.03] border border-white/5 p-4">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <stat.icon className={`w-4 h-4 ${stat.color}`} />
                    <p className={`text-2xl sm:text-3xl font-bold ${stat.color}`}>{stat.value}</p>
                  </div>
                  <p className="text-xs text-slate-500">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Value Statement Bar */}
      <section className="py-10 border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            <div>
              <p className="text-white font-semibold mb-1">AI Generates. You Refine.</p>
              <p className="text-sm text-slate-500">Every output is a starting point — you control the final answer</p>
            </div>
            <div>
              <p className="text-white font-semibold mb-1">Live in Days, Not Months.</p>
              <p className="text-sm text-slate-500">No consultants, no migration projects, no six-month timelines</p>
            </div>
            <div>
              <p className="text-white font-semibold mb-1">From Analysis to Action.</p>
              <p className="text-sm text-slate-500">Scenario plans, financial models, and board reports — connected end to end</p>
            </div>
          </div>
        </div>
      </section>

      {/* Four Pillars Section */}
      <section id="pillars" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge variant="outline" className="text-indigo-400 border-indigo-500/30 mb-4">Complete Platform</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
              Four Pillars of Risk Optimization
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto">
              Not a point solution. A complete operating system for risk — from identification 
              through financial optimization and board reporting.
            </p>
          </div>

          {/* Pillar Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {pillars.map((pillar, i) => (
              <button
                key={i}
                onClick={() => setActivePillar(i)}
                data-testid={`pillar-tab-${i}`}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  activePillar === i
                    ? `${pillar.bgClass} ${pillar.colorClass} border ${pillar.borderClass}`
                    : "bg-white/[0.03] text-slate-500 border border-white/5 hover:bg-white/[0.06] hover:text-slate-300"
                }`}
              >
                <pillar.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{pillar.title}</span>
                <span className="sm:hidden">{pillar.title.split(" ")[0]}</span>
              </button>
            ))}
          </div>

          {/* Active Pillar Content */}
          <motion.div
            key={activePillar}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
              {/* Pillar Header */}
              <div className="px-8 py-6 border-b border-white/5">
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-lg ${active.bgClass} flex items-center justify-center`}>
                    <active.icon className={`w-5 h-5 ${active.colorClass}`} />
                  </div>
                  <h3 className={`text-xl font-bold ${active.colorClass}`}>{active.title}</h3>
                </div>
                <p className="text-slate-400">{active.tagline}</p>
              </div>

              {/* Tools Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0">
                {active.tools.map((tool, i) => (
                  <div
                    key={i}
                    className="p-6 border-b border-r border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <tool.icon className={`w-4 h-4 mt-0.5 shrink-0 ${active.colorClass}`} />
                      <div>
                        <p className="text-sm font-semibold text-white mb-1">{tool.name}</p>
                        <p className="text-xs text-slate-500 leading-relaxed">{tool.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {active.title === "Financial Risk Optimization" && (
                <div className="px-8 py-3 border-t border-white/5 bg-white/[0.01] text-[11px] text-slate-500 italic flex items-start gap-2">
                  <Lock className="w-3 h-3 mt-0.5 shrink-0 text-emerald-400/70" />
                  <span>
                    Actuarial-grade methods, <span className="text-slate-300">directional output</span>.
                    Built to scope, stress-test, and inform decisions between formal engagements —
                    not a substitute for a credentialed Statement of Actuarial Opinion.
                  </span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Pillar Summary Cards (always visible) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            {pillars.map((p, i) => (
              <button
                key={i}
                onClick={() => setActivePillar(i)}
                className={`p-4 rounded-xl text-left transition-all duration-300 ${
                  activePillar === i
                    ? `bg-white/[0.04] border ${p.borderClass}`
                    : "bg-white/[0.02] border border-white/5 hover:bg-white/[0.03]"
                }`}
              >
                <p.icon className={`w-5 h-5 mb-2 ${activePillar === i ? p.colorClass : "text-slate-600"}`} />
                <p className={`text-xs font-semibold ${activePillar === i ? p.colorClass : "text-slate-500"}`}>
                  {p.title}
                </p>
                <p className="text-[10px] text-slate-600 mt-1">{p.tools.length} tools</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section id="roi" className="py-24 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 mb-4">Return on Investment</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
              An ERM Program in Days, Not Years.
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto">
              Stand up a full enterprise risk program — risk register, KRIs, bow-ties, financial
              modeling, and board reporting — in the time it takes most consultants to send a kickoff deck.
              Compress the decision cycle, not the credential.
            </p>
          </div>

          <div className="rounded-xl border border-white/10 overflow-hidden">
            <div className="grid grid-cols-4 gap-0 bg-white/[0.03] border-b border-white/10 p-4 text-sm font-medium">
              <div className="text-slate-400">Analysis</div>
              <div className="text-slate-400 text-center">Traditional Cost</div>
              <div className="text-slate-400 text-center">Traditional Timeline</div>
              <div className="text-center text-indigo-400">With FractionalRisk.ai</div>
            </div>
            {comparisonPoints.map((row, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="grid grid-cols-4 gap-0 border-b border-white/5 p-4 text-sm hover:bg-white/[0.02] transition-colors"
              >
                <div className="text-white font-medium">
                  {row.label}
                  {row.note && (
                    <div className="text-[11px] text-slate-500 font-normal mt-1 italic">{row.note}</div>
                  )}
                </div>
                <div className="text-center text-slate-500">{row.theirCost}</div>
                <div className="text-center text-slate-500">{row.theirTime}</div>
                <div className="text-center">
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 font-semibold text-xs">
                    <Clock className="w-3 h-3" />
                    {row.you}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-600 mt-4">
            Cost estimates based on industry averages for consulting and GRC implementations.
            FractionalRisk.ai provides directional analysis to inform strategic decisions — not credentialed
            actuarial, legal, or audit opinions.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge variant="outline" className="text-cyan-400 border-cyan-500/30 mb-4">How It Works</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
                AI Does the Heavy Lifting.<br />
                <span className="text-cyan-400">You Make the Decisions.</span>
              </h2>
              <p className="text-base text-slate-400 mb-10 leading-relaxed">
                Every module is AI-assisted but human-controlled. The platform generates 
                structured analysis — you review, refine, and act. No black boxes.
              </p>
              <div className="space-y-6">
                {[
                  { step: "01", title: "Identify", desc: "AI scans your risk landscape — external threats, internal controls, emerging trends. Risks are auto-scored and categorized.", color: "text-indigo-400" },
                  { step: "02", title: "Analyze", desc: "Bow-tie diagrams, scenario models, Monte Carlo simulations, and strategic alignment — generated in seconds, refined by your team.", color: "text-cyan-400" },
                  { step: "03", title: "Optimize", desc: "Financial modeling tells you the optimal mix of retention, transfer, and controls. Pro formas and benchmarks quantify every decision.", color: "text-emerald-400" },
                  { step: "04", title: "Report", desc: "Board-ready reports, KRI dashboards, and action tracking keep stakeholders informed and accountable. One click, not one week.", color: "text-amber-400" },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4"
                  >
                    <span className={`text-2xl font-bold ${item.color} opacity-40 shrink-0 w-8`}>{item.step}</span>
                    <div>
                      <p className={`text-sm font-semibold ${item.color} mb-1`}>{item.title}</p>
                      <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* AI Analyst Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/10 to-cyan-600/10 rounded-3xl blur-3xl" />
              <div className="relative bg-[#0c0f1a] rounded-2xl border border-white/10 p-6 space-y-3">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">AI Risk Analyst</p>
                    <p className="text-slate-500 text-xs">Always on, always current</p>
                  </div>
                </div>
                <div className="bg-white/[0.03] rounded-lg p-3 border border-indigo-500/10">
                  <p className="text-[10px] text-indigo-400 mb-1 uppercase tracking-wider">Scenario Analysis</p>
                  <p className="text-sm text-white">Supply chain disruption scenario: 3 cascading effects identified, 4 early warning signs flagged, 5 mitigation paths recommended</p>
                </div>
                <div className="bg-white/[0.03] rounded-lg p-3 border border-cyan-500/10">
                  <p className="text-[10px] text-cyan-400 mb-1 uppercase tracking-wider">Bow-Tie Generated</p>
                  <p className="text-sm text-white">4 root causes identified, 6 controls mapped, 3 consequences quantified — linked to your control library</p>
                </div>
                <div className="bg-white/[0.03] rounded-lg p-3 border border-emerald-500/10">
                  <p className="text-[10px] text-emerald-400 mb-1 uppercase tracking-wider">Financial Optimization</p>
                  <p className="text-sm text-white">Captive breakeven at Year 3. NPV of $2.4M over 5 years. GL retention at $250K optimal for your loss experience.</p>
                </div>
                <div className="bg-white/[0.03] rounded-lg p-3 border border-amber-500/10">
                  <p className="text-[10px] text-amber-400 mb-1 uppercase tracking-wider">Insurance Program Alert</p>
                  <p className="text-sm text-white">Tower Layer 2 excess carrier renewal in 45 days. Your loss ratio of 68% is above 75th percentile — consider program review before renewal.</p>
                </div>
                <div className="bg-white/[0.04] rounded-lg p-3 border border-white/10">
                  <p className="text-[10px] text-slate-400 mb-1 uppercase tracking-wider">Decision Coach</p>
                  <p className="text-sm text-white">Based on your scenario analysis and financial models, engaging an actuary for a formal captive feasibility study is justified. Here's your data package.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
            <Lock className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-400">Enterprise-Grade Security</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
            Your Data, Your Instance, Your Control
          </h2>
          <p className="text-base text-slate-400 max-w-2xl mx-auto mb-12">
            Purpose-built for risk and financial data. Per-customer isolation. 
            ISO 31000 and COSO framework aligned out of the box.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { icon: Lock, label: "SSO / OKTA Ready", desc: "Enterprise authentication" },
              { icon: Shield, label: "Data Isolation", desc: "Per-customer instances" },
              { icon: Globe, label: "Private Deployment", desc: "Your cloud or ours" },
              { icon: FileText, label: "Framework Aligned", desc: "ISO 31000 & COSO" },
            ].map((item, i) => (
              <div key={i} className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
                <item.icon className="w-6 h-6 text-emerald-400 mx-auto mb-3" />
                <p className="text-white font-medium text-sm">{item.label}</p>
                <p className="text-xs text-slate-500 mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
          <Link to="/security">
            <Button variant="outline" className="mt-10 border-emerald-500/30 hover:bg-emerald-500/10 text-emerald-400" data-testid="security-learn-more-btn">
              How We Protect Your Data
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
            See What Risk Optimization Looks Like
          </h2>
          <p className="text-base text-slate-400 mb-8">
            30-minute demo. Real analysis on your industry. No commitment.
          </p>
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 text-left">
            {submitted ? (
              <div className="py-4 text-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                <p className="text-white font-semibold text-lg">Thank you!</p>
                <p className="text-sm text-slate-400 mt-1">We'll be in touch shortly to schedule your personalized demo.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <input
                    type="text"
                    placeholder="Full name *"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 transition-colors"
                    data-testid="marketing-name-input"
                  />
                  <input
                    type="email"
                    placeholder="Work email *"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 transition-colors"
                    data-testid="marketing-email-input"
                  />
                  <input
                    type="text"
                    placeholder="Company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 transition-colors"
                    data-testid="marketing-company-input"
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 transition-colors"
                    data-testid="marketing-phone-input"
                  />
                </div>
                <textarea
                  placeholder="Tell us about your risk management needs (optional)"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 transition-colors mb-4 resize-none"
                  data-testid="marketing-message-input"
                />
                {error && (
                  <p className="text-xs text-red-400 mb-3" data-testid="marketing-form-error">{error}</p>
                )}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                  <p className="text-xs text-slate-600 sm:flex-1">
                    30-minute walkthrough · Real analysis on your industry · No commitment.
                  </p>
                  <Button
                    size="lg"
                    className="glow-primary whitespace-nowrap"
                    onClick={handleDemoRequest}
                    disabled={submitting}
                    data-testid="marketing-demo-btn"
                  >
                    {submitting ? "Sending..." : "Request Demo"}
                    {!submitting && <ChevronRight className="w-5 h-5 ml-1" />}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/540aeb3a-1114-4f99-bc37-f6fae5b5d364/images/ebc8ab9a8b9b87e3f3e4c74c7286555b4503165514e41967f2693644773011b6.png"
                alt="FractionalRisk.ai Logo"
                className="h-8 w-auto"
              />
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-600 flex-wrap">
              <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <Link to="/legal/cookies" className="hover:text-white transition-colors" data-testid="footer-cookies-link">Cookies</Link>
              <Link to="/legal/dpa" className="hover:text-white transition-colors" data-testid="footer-dpa-link">DPA</Link>
              <Link to="/security" className="hover:text-white transition-colors" data-testid="footer-security-link">Security</Link>
              <a href="#contact" className="hover:text-white transition-colors">Contact</a>
            </div>
            <p className="text-sm text-slate-600">
              &copy; 2025 FractionalRisk.ai. All rights reserved.
            </p>
          </div>
          <p className="text-[11px] text-slate-600 mt-6 max-w-4xl mx-auto text-center leading-relaxed">
            FractionalRisk.ai delivers AI-generated, directional analysis to inform strategic risk and
            financing decisions. Outputs are not a Statement of Actuarial Opinion and do not constitute
            legal, tax, audit, or regulatory advice. Engage credentialed professionals for formal opinions.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MarketingSite;
